import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/system';
import { useTranslation } from '@hooks';
import PageContainer from '@components/Layout/PageContainer';
import { dashboardActions, accountActions } from '@actions';
import usePermissions from '@hooks/usePermissions';
import { Recommendation } from '@constants/dashboard';
import { RoutePath } from '@constants/routes';
import SpeedIcon from '@mui/icons-material/Speed';
import { LeaderboardIcon } from 'assets/images/icons';
import { ReviewsIcon, SocialIcon, MessagingIcon, SurveysIcon } from 'assets/images/sidebar';
import { LocationsIcon, SynchronizationIcon } from 'assets/images/icons';
import Leaderboard from './Leaderboard';
import SectionWrapper from './SectionWrapper';
import Reviews from './Reviews';
import Inbox from './Inbox';
import Surveys from './Surveys';
import Social from './Social';
import SurveysDisabled from './SurveysDisabled';
import Onboarding from './Onboarding';
import Synchronization from './Synchronization';
import OptimizationScore from './OptimizationScore';
import Presence from './Presence';
import CriticalAlerts from './CriticalAlerts';
import { SORT_TYPE, LEADERBOARD_TYPE } from './Leaderboard';

const Content = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridTemplateAreas: '"column1 column2" "leaderboard leaderboard"',
    gridGap: 16,
    '@media (max-width: 850px)': {
        gridTemplateColumns: '100%',
        gridTemplateAreas: '"column1" "column2" "leaderboard"',
    },
});

const HomePage = () => {
    const { t } = useTranslation();
    const account = useSelector(state => state.account?.account);
    const accountOnboarding = useSelector(state => state.account.onboarding);
    const { hasAccess, hasFullAccess } = usePermissions();

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(accountActions.getDashboardOnboarding());
        dispatch(dashboardActions.getDashboardAnalytics());
        dispatch(dashboardActions.getLeaderboardAnalytics(SORT_TYPE.REVIEWS, LEADERBOARD_TYPE.TOP));
        dispatch(dashboardActions.getRecommendations());
    }, []);

    const accountOnboardingCompleted = Object.values(accountOnboarding).every(item => !!item);
    const hasLocationsFullAccess = hasFullAccess('locations');
    const optimizationRecs = hasLocationsFullAccess ? [Recommendation.IncompleteLocations] : [];

    return (
        <PageContainer>
            {hasLocationsFullAccess && !accountOnboardingCompleted ? (
                <Onboarding account={account} accountOnboarding={accountOnboarding} />
            ) : null}
            <CriticalAlerts />
            <Content>
                <div style={{ gridArea: 'column1' }}>
                    {hasAccess('locations') && (
                        <>
                            <SectionWrapper
                                url={RoutePath.LocationsManage}
                                text={t('SettingsCompanies.optimizationScore')}
                                subtitle={t('HomePage.optimizationScoreSubtitle')}
                                icon={SpeedIcon}
                                recommendationTypes={optimizationRecs}
                            >
                                <OptimizationScore />
                            </SectionWrapper>
                            <SectionWrapper
                                url={RoutePath.LocationsAnalytics}
                                text={t('HomePage.locations')}
                                subtitle={t('HomePage.locationsSubtitle')}
                                icon={LocationsIcon}
                                recommendationTypes={[Recommendation.OutdatedPhotos]}
                            >
                                <Presence />
                            </SectionWrapper>
                            <SectionWrapper
                                url={RoutePath.LocationsProfiles}
                                text={t('HomePage.synchronization')}
                                subtitle={t('HomePage.synchronizationSubtitle')}
                                icon={SynchronizationIcon}
                                recommendationTypes={[Recommendation.ActionRequiredProfiles]}
                            >
                                <Synchronization />
                            </SectionWrapper>
                        </>
                    )}
                    {(hasAccess('reviews') || hasAccess('surveys')) && (
                        <SectionWrapper
                            withLink={false}
                            text={t('HomePage.leaderboard')}
                            subtitle={t('HomePage.leaderboardSubtitle')}
                            icon={LeaderboardIcon}
                        >
                            <Leaderboard surveysEnabled={account?.surveysEnabled && hasAccess('surveys')} />
                        </SectionWrapper>
                    )}
                </div>
                <div style={{ gridArea: 'column2' }}>
                    {hasAccess('messaging') && (
                        <SectionWrapper
                            url={RoutePath.MessagingInbox}
                            text={t('HomePage.inbox')}
                            subtitle={t('HomePage.inboxSubtitle')}
                            icon={MessagingIcon}
                            recommendationTypes={[Recommendation.UnreadConversations]}
                        >
                            <Inbox />
                        </SectionWrapper>
                    )}
                    {hasAccess('reviews') && (
                        <SectionWrapper
                            url={RoutePath.ReviewsManage}
                            text={t('Sidebar.reviews')}
                            subtitle={t('HomePage.reviewsSubtitle')}
                            icon={ReviewsIcon}
                            recommendationTypes={[Recommendation.UnrepliedReviews]}
                        >
                            <Reviews />
                        </SectionWrapper>
                    )}
                    {hasAccess('surveys') &&
                        (account?.surveysEnabled ? (
                            <SectionWrapper
                                url={RoutePath.SurveysResponses}
                                text={t('HomePage.surveys')}
                                subtitle={t('HomePage.surveysSubtitle')}
                                icon={SurveysIcon}
                                recommendationTypes={[Recommendation.UnreadSurveyResponses]}
                            >
                                <Surveys />
                            </SectionWrapper>
                        ) : (
                            <SurveysDisabled />
                        ))}
                    {hasAccess('posts') && (
                        <SectionWrapper
                            url={RoutePath.SocialPosts}
                            text={t('Sidebar.socialPosts')}
                            subtitle={t('HomePage.socialSubtitle')}
                            icon={SocialIcon}
                            recommendationTypes={[Recommendation.OutdatedSocialPosts]}
                        >
                            <Social />
                        </SectionWrapper>
                    )}
                </div>
            </Content>
        </PageContainer>
    );
};

export default HomePage;
